import { toast } from 'sonner'
import { useMemo, useState } from 'react'

import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'

import { useUserInfo } from 'src/hooks/useUserInfo'
import { useManageServiceContacts } from 'src/hooks/useManageServiceContacts'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { useAppDispatch } from 'src/hooks/useAppDispatch'
import { serviceTypeDictionary } from 'src/helpers/serviceTypeDictionaryForContacts'

import {
  getFavoriteTexts,
  getTableHeaders,
} from 'src/features/TableForContactsHistory/helpers'
import { queryClient } from 'src/features/AppProviders/constants'
import { FullServiceContactsProps } from 'src/api/ServiceContacts/types'

import { userSlice } from 'src/store/reducers/UserReducer/UserSlice'

export const useTableForContactsHistory = (
  allContactsHistory: FullServiceContactsProps[],
) => {
  const { translations } = useLocales()
  const { updateContact, deleteContact } = useManageServiceContacts()
  const {
    userInfo: { favoriteLimit },
  } = useUserInfo()

  const [editModeMap, setEditModeMap] = useState<Record<string, boolean>>({})

  const dispatch = useAppDispatch()
  const { setIsStarredHide, setIsContactsHide } = userSlice.actions
  const { isStarredHide, isContactsHide } = useAppSelector(state => state.user)

  const handleHideStarred = (): void => {
    dispatch(setIsStarredHide(!isStarredHide))
  }

  const handleHideContacts = (): void => {
    dispatch(setIsContactsHide(!isContactsHide))
  }

  const starredContacts = useMemo(
    () => allContactsHistory.filter(contact => contact.isFavorite),
    [allContactsHistory],
  )

  const nonStarredContacts = useMemo(
    () => allContactsHistory.filter(contact => !contact.isFavorite),
    [allContactsHistory],
  )

  const handleFavorite = (
    id: string,
    currentContact: FullServiceContactsProps,
  ) => {
    if (!currentContact.isFavorite && starredContacts.length >= favoriteLimit) {
      toast(
        translations.serviceContacts.historyServiceContacts.toast.favorite.limit
          .title,
        {
          description:
            translations.serviceContacts.historyServiceContacts.toast.favorite
              .limit.description,
          action: {
            label:
              translations.serviceContacts.historyServiceContacts.toast.favorite
                .limit.action.label,
            onClick: () => toast.dismiss(),
          },
        },
      )

      return
    }

    const updateItem: FullServiceContactsProps = {
      ...currentContact,
      isFavorite: !currentContact.isFavorite,
    }

    updateContact(
      { payload: updateItem, id: id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.SERVICE_CONTACTS],
          })

          const favoriteTexts = getFavoriteTexts(
            translations.serviceContacts,
            updateItem.isFavorite,
          )

          toast(favoriteTexts.title, {
            description: favoriteTexts.description,
            action: {
              label: favoriteTexts.action.label,
              onClick: () => toast.dismiss(),
            },
          })
        },
        onError: error => {
          toast(
            translations.serviceContacts.historyServiceContacts.toast.error
              .title,
            {
              description:
                translations.serviceContacts.historyServiceContacts.toast.error
                  .description + error,
              action: {
                label:
                  translations.serviceContacts.historyServiceContacts.toast
                    .error.action.label,
                onClick: () => toast.dismiss(),
              },
            },
          )
        },
      },
    )
  }

  const handleDelete = (itemId: string): void => {
    deleteContact(itemId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [StorePersistKeys.SERVICE_CONTACTS],
        })
        toast(
          translations.serviceContacts.historyServiceContacts.toast.success
            .title,
          {
            description:
              translations.serviceContacts.historyServiceContacts.toast.success
                .description,
            action: {
              label:
                translations.serviceContacts.historyServiceContacts.toast
                  .success.action.label,
              onClick: () => toast.dismiss(),
            },
          },
        )
      },
      onError: error => {
        toast(
          translations.serviceContacts.historyServiceContacts.toast.error.title,
          {
            description:
              translations.serviceContacts.historyServiceContacts.toast.error
                .description + error,
            action: {
              label:
                translations.serviceContacts.historyServiceContacts.toast.error
                  .action.label,
              onClick: () => toast.dismiss(),
            },
          },
        )
      },
    })
  }

  const toggleEditMode = (contactId: string): void => {
    setEditModeMap(prevState => ({
      ...prevState,
      [contactId]: !prevState[contactId],
    }))
  }

  return {
    locale: translations.serviceContacts.historyServiceContacts,
    titleStarred:
      translations.serviceContacts.historyServiceContacts.table.favoriteTitle,
    titleNonStared:
      translations.serviceContacts.historyServiceContacts.table.contacts,
    handleDelete,
    tableHeaders: getTableHeaders(translations.serviceContacts),
    serviceTypeDictionary: serviceTypeDictionary(translations.serviceContacts),
    editModeMap,
    toggleEditMode,
    handleFavorite,
    starredContacts,
    nonStarredContacts,
    handleHideStarred,
    isStarredHide,
    handleHideContacts,
    isContactsHide,
  }
}
